import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { Box } from '@material-ui/core';
import { DashboardWidget } from './DashboardWidget';
import type { NameValueItem } from './types';

type AttentionByEntityChartProps = {
  limaData: NameValueItem[];
  provinciaData: NameValueItem[];
  loading?: boolean;
};

const COLORS = ['#062b50ff', '#0b4a8b', '#2d6aa1', '#5f87ad', '#82c6fa9f', '#92acc1', '#c7d4df'];
const RADIAN = Math.PI / 180;

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.78;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#5f6368"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={14}
      fontWeight={500}
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const DonutChartBlock = ({ title, data }: { title: string; data: NameValueItem[] }) => (
  <Box
    style={{
      flex: 1,
      minWidth: 320,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    }}
  >
    <Box
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 220, height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={75}
              paddingAngle={2}
              labelLine={false}
              label={renderCustomLabel}
            >
              {data.map((entry, index) => (
                <Cell key={`${title}-${entry.name}-${index}` as string} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div style={{ minWidth: 150 }}>
        {data.map((item, index) => (
          <div
            key={`${title}-legend-${item.name}-${index}` as string}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 10,
              fontSize: 10,
              color: '#5f6368',
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: COLORS[index % COLORS.length],
                display: 'inline-block',
                marginRight: 10,
                flexShrink: 0,
              }}
            />
            <span>{item.name}</span>
          </div>
        ))}
      </div>
    </Box>

    <div
      style={{
        textAlign: 'left',
        marginTop: 6,
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: 1,
        color: '#4a4a4a',
        marginLeft: '15%',
      }}
    >
      {title}
    </div>
  </Box>
);

export const AttentionByEntityChart = ({ limaData, provinciaData, loading = false }: AttentionByEntityChartProps) => (
  <DashboardWidget
    title="ATENCION POR ENTIDAD"
    loading={loading}
    hasData={limaData.length > 0 || provinciaData.length > 0}
  >
    <Box
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      {limaData.length > 0 && <DonutChartBlock title="EN LIMA" data={limaData} />}
      {provinciaData.length > 0 && <DonutChartBlock title="EN PROVINCIA" data={provinciaData} />}
    </Box>
  </DashboardWidget>
);
